import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { authLog, authTrail, fingerprint } from 'src/sentry';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

import { requestFromApp } from 'src/utils/post-to-app';
import { DIRECTIVE_CAPABILITIES_HEADER, directiveCapabilitiesHeaderValue } from 'src/utils/directive-capabilities';

import { useAuth } from './use-auth';
import { useUser } from './use-user';

export interface ApiInstance extends AxiosInstance {
  event: (event: string | string[], flow?: string) => Promise<void>;
}

const noAuthorization = [
  'auth/login/',
  'auth/registration/',
  'v1/projects/',
  'auth/password/reset/',
  'auth/password/reset/confirm/',
  'login/bp-scan/',
  'login/magic/',
  'login/request-magic-link/',
  'login/request-otp/',
  'login/verify-otp/',
];

const useAxios = (): ApiInstance => {
  const { auth, setAuth, clearedToken } = useAuth();
  const { lang } = useUser();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlFlow = queryParams.get('flow');

  return useMemo(() => {
    const apiInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    }) as ApiInstance;

    apiInstance.interceptors.request.use(
      (config) => {
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Token ${auth}`;
        }
        if (noAuthorization.includes(config.url as string)) {
          config.headers.Authorization = null;
        }
        config.headers['Accept-Language'] = lang;
        // PROD-1832: advertise renderable directive types on every request so
        // the backend (PROD-1831) can filter directives at serialization time.
        const capabilities = directiveCapabilitiesHeaderValue();
        if (capabilities) config.headers[DIRECTIVE_CAPABILITIES_HEADER] = capabilities;
        authTrail('request', { url: config.url, method: config.method, hasAuth: !!auth });
        return config;
      },
      (error) => Promise.reject(error)
    );

    apiInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const status = error?.response?.status;
        const url = error.config?.url;

        if (status === 401) {
          const failedToken = (error.config?.headers?.Authorization as string | undefined)?.slice('Token '.length);
          authLog('401', { url, method: error.config?.method, status: 401, failedTokenFp: fingerprint(failedToken) });

          // Ask native for a token and classify the outcome into a named string so the
          // recovery vs. silent-logout decision is searchable in Sentry.
          let outcome = 'no_webview';
          let token: string | undefined;
          if (window.ReactNativeWebView) {
            ({ token } = (await requestFromApp('GET_AUTH_TOKEN')) as { token?: string });
            if (!token) outcome = 'missing_or_timeout';
            else if (token === failedToken) outcome = 'same_as_failed';
            else if (token === clearedToken.current) outcome = 'cleared_token_match';
            else outcome = 'recovered';
            authTrail('native token outcome', { outcome, nativeTokenFp: fingerprint(token) });
          }

          if (outcome === 'recovered') {
            setAuth(token);
            return Promise.reject(error);
          }

          authLog('auth:clear via 401', { url, reason: outcome });
          setAuth(null);
        }
        return Promise.reject(error);
      }
    );

    apiInstance.event = async (event, flow) => {
      // event can be either a string or an array of strings
      const events = Array.isArray(event) ? event : [event];
      await events.reduce<Promise<AxiosResponse | void>>(async (previousPromise, e) => {
        await previousPromise;

        return apiInstance.post(`v1/events/`, {
          flow: urlFlow || flow || 'additional_onboarding_v4',
          event: e,
        });
      }, Promise.resolve());
    };

    return apiInstance;
  }, [auth, lang, setAuth, clearedToken, urlFlow]);
};

export default useAxios;
